import { InjectionToken } from '@angular/core';

/**
 * Brand-specific icon-variant suffixes for the textboard paging buttons (`canvas-textboard-slides`,
 * design D5). Relocated from the retiring `ui-svg-image` lib (`SLIDE_SYNC_UI_SVG_TEXTBOARD_CONFIG`)
 * so the canvas path does not depend on it; the apps provide it per brand in
 * `provide-config.ts` (PCP: '', 'green', 'gray'; TUT: 'brackground-*'). Suffixes expand to
 * `assets/icons/svg/arrow-{left|right}[-suffix].svg` (icons shipped via `libs/styles/slide-sync/assets`).
 */
export interface SlideCanvasTextboardIconConfig {
    pagingButtons: {
        normal: string;
        hover: string;
        disabled: string;
    };
    /**
     * Brand-configurable edge offsets of the text render area (px, 1280×720 logical viewport) —
     * the canvas twin of develop's `SvgTextboardConfig.layout`. Left/top are fixed; these define
     * the right/bottom edges. PCP reserves a wide right column (400) and runs to the bottom (30);
     * TUT runs full width (20) but stops above the paging buttons (100).
     */
    layout: {
        textOffsetRight: number;
        textOffsetBottom: number;
    };
}

export const SLIDE_CANVAS_TEXTBOARD_ICON_CONFIG = new InjectionToken<SlideCanvasTextboardIconConfig>(
    'SLIDE_CANVAS_TEXTBOARD_ICON_CONFIG',
    {
        providedIn: 'root',
        // Fallback = the PCP icon set + PCP text area, so the renderer works without an explicit provider.
        factory: () => ({
            pagingButtons: { normal: '', hover: 'green', disabled: 'gray' },
            layout: { textOffsetRight: 400, textOffsetBottom: 30 },
        }),
    },
);

/** Resolved per-state icon URLs for one paging direction. */
export interface PagingIconStateUrls {
    enabled: string;
    hover: string;
    disabled: string;
}

/** Resolved icon URLs for both paging buttons — what the capability actually loads. */
export interface TextboardPagingIcons {
    prev: PagingIconStateUrls;
    next: PagingIconStateUrls;
}

/**
 * Expand the brand suffix config into concrete icon URLs. Relative to the document base URI
 * (respects `<base href>`, like every other `assets/` reference).
 */
export function resolvePagingIconUrls(config: SlideCanvasTextboardIconConfig): TextboardPagingIcons {
    const icon = (direction: 'left' | 'right', suffix: string) =>
        `assets/icons/svg/arrow-${direction}${suffix ? `-${suffix}` : ''}.svg`;
    const forDirection = (direction: 'left' | 'right'): PagingIconStateUrls => ({
        enabled: icon(direction, config.pagingButtons.normal),
        hover: icon(direction, config.pagingButtons.hover),
        disabled: icon(direction, config.pagingButtons.disabled),
    });
    return { prev: forDirection('left'), next: forDirection('right') };
}
